import { useEffect } from "react";
import { useParams } from "react-router";
import styled from "styled-components/macro";
import { motion } from "framer-motion";
import { categoryContent, data } from "./data/data";
import SubRadar from "./SubRadar";
import BackButton from "./BackButton";

const StyledContent = styled.div`
  font-family: poppins, sans-serif;
  text-align: left;
  width: 100%;
  display: inline-block;
  overflow: hidden;
  min-height: 100vh;

  div.content {
    margin: auto;
    margin-top: 50px;
    width: 80vw;
    max-width: 800px;
  }

  @media screen and (max-width: 1000px) {
    margin: 10px 10px;
    div.content {
      margin-top: 0px;
      width: 90vw;
    }
  }
`;

const Title = styled(motion.h2)`
  color: ${({ theme }) => theme.titleColor};
  font-family: "DM Serif Display", serif;
  font-size: 100px;
  letter-spacing: 1px;
  font-weight: 400;
  padding: 0 0 40px 0;
  margin: auto;

  @media screen and (max-width: 1000px) {
    font-size: 70px;
    padding: 20px 0 20px 0;
  }
`;

const Description = styled(motion.p)`
  margin: 0;
  max-width: 520px;
  font-size: 24px;
  padding: 10px 0 50px 0;
  text-align: left;
  color: #5570a2ff;
  font-weight: 400;
  line-height: 2;
  font-family: "EB Garamond", serif;
`;

const StyledLink = styled(motion.div)`
  padding-bottom: 8px;
  a {
    color: #506dafff;
    text-decoration: none !important;
    font-family: poppins, sans-serif;
    font-size: 16px;
    display: flex;
    align-items: center;
    transition: color 0.2s;

    .tiny-arrow {
      margin: 0px 8px 0px -10px;
      opacity: 0;
      font-size: 20px;
      transition: margin 0.3s cubic-bezier(0.39, 0.575, 0.565, 1), opacity 0.3s;
    }

    :hover {
      color: #4c80faff;
      .tiny-arrow {
        margin: 0px 8px 0px 10px;
        opacity: 1;
      }
    }
  }
`;

// Motion variants
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.39, 0.575, 0.565, 1],
    },
  },
};

const TechStackSubPage = () => {
  const { categoryId } = useParams();

  const pageContent = categoryContent.find(
    ({ categoryName }) =>
      categoryName.replace(/[\s/]/g, "").toLowerCase() === categoryId
  );

  const radarData = data.find(
    ({ name }) => categoryId === name.replace(/[\s/]/g, "").toLowerCase()
  );

  useEffect(() => {
    window.scroll({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  if (!pageContent || !radarData) return null;

  return (
    <StyledContent>
      <motion.div
        className="content"
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={itemVariants}>
          <BackButton title="Back to Radar" to="/techstack" />
        </motion.div>

        <Title variants={itemVariants}>{radarData.name}</Title>

        <Description variants={itemVariants}>
          {pageContent.description}
        </Description>

        <motion.div
          style={{
            fontSize: 16,
            fontFamily: "poppins, sans-serif",
            fontWeight: 400,
            color: "#1d5bcea0",
          }}
          variants={itemVariants}
        >
          EXPLORE PROJECTS
        </motion.div>

        <motion.ul
          style={{ listStyle: "none", padding: 0, margin: "24px 0 0 0" }}
          variants={containerVariants}
        >
          {pageContent.projects.map(({ title, url }) => (
            <StyledLink
              key={title}
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
            >
              <a href={url} target="_blank" rel="noopener noreferrer">
                {title}
                <span className="tiny-arrow">→</span>
              </a>
            </StyledLink>
          ))}
        </motion.ul>
        <SubRadar data={radarData.data} />
      </motion.div>
    </StyledContent>
  );
};

export default TechStackSubPage;
